import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShoppingCart, Heart, Package, Ruler, Tag } from "lucide-react";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || "https://lirkprint-backend.onrender.com").replace(/\/$/, "");
const API = `${BASE_URL}/api`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { user } = useAuth();
  const { lang, t } = useLanguage();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [inWishlist, setInWishlist] = useState(false);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id, user]);

  const fetchProduct = async () => {
    try {
      const { data } = await axios.get(`${API}/products/${id}`);
      setProduct(data);
      if (user) {
        try {
          const { data: wl } = await axios.get(`${API}/wishlist`, { withCredentials: true });
          const exists = (wl.items || []).some(p => p.id === id);
          setInWishlist(exists);
        } catch {
          // Wishlist fetch error ignored
        }
      }
    } catch {
      navigate("/shop");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    addItem(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleToggleWishlist = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      if (inWishlist) {
        await axios.delete(`${API}/wishlist/${id}`, { withCredentials: true });
        setInWishlist(false);
      } else {
        await axios.post(`${API}/wishlist/${id}`, {}, { withCredentials: true });
        setInWishlist(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-foreground/40">{t("product.not_found")}</p>
    </div>
  );

  const name = lang === "de" ? product.name_de : product.name_en;
  const desc = lang === "de" ? product.description_de : product.description_en;
  const validImages = (product.images || []).filter(img => Boolean(img && img.trim()));
  const images = validImages.length ? validImages : ["https://images.unsplash.com/photo-1593642632559-0c6d3fc62b89?w=800&q=80"];

  return (
    <div className="min-h-screen bg-background pt-24">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Back */}
        <button
          data-testid="back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-foreground/40 hover:text-foreground transition-colors mb-10 font-mono tracking-wide"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> {t("product.back")}
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="aspect-square bg-surface border border-border rounded-sm overflow-hidden mb-4">
              <img
                src={images[activeImg]}
                alt={name}
                className="w-full h-full object-cover"
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-2">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`w-16 h-16 rounded-sm overflow-hidden border transition-all duration-300 ${activeImg === i ? "border-foreground" : "border-border opacity-50 hover:opacity-100"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="flex flex-col"
          >
            {product.status === "featured" && (
              <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-foreground/40 mb-4">
                {t("product.featured")}
              </span>
            )}

            <h1 className="text-4xl font-heading font-bold tracking-tighter text-foreground mb-4">{name}</h1>

            <div className="text-3xl font-mono font-light text-foreground mb-6">
              €{product.price?.toFixed(2)}
            </div>

            <p className="text-base text-foreground/60 leading-relaxed mb-8">{desc}</p>

            {/* Details */}
            <div className="grid grid-cols-2 gap-4 mb-8 p-4 border border-border rounded-sm">
              {product.material && (
                <div className="flex items-center gap-2">
                  <Package size={14} strokeWidth={1.5} className="text-foreground/30" />
                  <div>
                    <p className="text-[10px] font-mono text-foreground/30 uppercase tracking-wider">{t("product.material")}</p>
                    <p className="text-sm text-foreground/80">{product.material}</p>
                  </div>
                </div>
              )}
              {product.dimensions && (
                <div className="flex items-center gap-2">
                  <Ruler size={14} strokeWidth={1.5} className="text-foreground/30" />
                  <div>
                    <p className="text-[10px] font-mono text-foreground/30 uppercase tracking-wider">{t("product.dimensions")}</p>
                    <p className="text-sm text-foreground/80">{product.dimensions}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Tag size={14} strokeWidth={1.5} className="text-foreground/30" />
                <div>
                  <p className="text-[10px] font-mono text-foreground/30 uppercase tracking-wider">{t("product.category")}</p>
                  <p className="text-sm text-foreground/80 capitalize">{product.category}</p>
                </div>
              </div>
              <div>
                <p className="text-[10px] font-mono text-foreground/30 uppercase tracking-wider mb-1">{t("product.in_stock")}</p>
                <p className={`text-sm font-mono ${product.stock > 0 ? "text-green-400" : "text-red-400"}`}>
                  {product.stock > 0 ? `${product.stock} ${t("product.stock_count")}` : t("product.out_of_stock")}
                </p>
              </div>
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6">
              <p className="text-xs font-mono text-foreground/40 uppercase tracking-wider">{t("product.quantity")}</p>
              <div className="flex items-center border border-border rounded-sm overflow-hidden">
                <button
                  data-testid="quantity-dec"
                  disabled={product.stock <= 0 || quantity <= 1}
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-3 py-2 text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  −
                </button>
                <span data-testid="quantity-value" className="px-4 py-2 text-foreground font-mono text-sm min-w-[40px] text-center border-x border-border">
                  {product.stock <= 0 ? 0 : quantity}
                </span>
                <button
                  data-testid="quantity-inc"
                  disabled={product.stock <= 0 || quantity >= product.stock}
                  onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                  className="px-3 py-2 text-foreground/60 hover:text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-3">
              <button
                data-testid="add-to-cart-btn"
                onClick={handleAddToCart}
                disabled={product.stock <= 0}
                className={`flex-1 flex items-center justify-center gap-2 py-4 font-mono text-sm tracking-widest uppercase rounded-sm transition-all duration-300 ${
                  added
                    ? "bg-green-500 text-white"
                    : product.stock <= 0
                    ? "bg-foreground/10 text-foreground/30 cursor-not-allowed"
                    : "bg-foreground text-background hover:bg-foreground/90"
                }`}
              >
                <ShoppingCart size={16} strokeWidth={1.5} />
                {added ? (lang === "de" ? "Hinzugefügt!" : "Added!") : t("product.add_to_cart")}
              </button>
              <button
                data-testid="wishlist-btn"
                onClick={handleToggleWishlist}
                className={`p-4 rounded-sm border transition-all duration-300 ${
                  inWishlist
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/50 hover:border-foreground/50 hover:text-foreground"
                }`}
              >
                <Heart size={16} strokeWidth={1.5} fill={inWishlist ? "currentColor" : "none"} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
